import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Container, CircularProgress, FormControlLabel, Checkbox, Link, Divider, Grid, TextField, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HighlightOff from '@mui/icons-material/HighlightOff';
import CountryPhoneInput from '../../components/common/CountryPhoneInput';
import { useAlert } from '../../contexts/AlertContext';
import api from '../../services/api';

// Single-page signup (no steps)

const SignupPage = () => {
  const [formData, setFormData] = useState({
    personal: {},
    account: {},
    agreedToTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { showAlert } = useAlert();

  const handleSubmit = async () => {
    setSubmitting(true);

    const payload = {
      "username": formData.account.username,
      "password": formData.account.password,
      "firstName": formData.personal.firstName,
      "lastName": formData.personal.lastName,
      "email": formData.personal.email,
      "phoneNumber": formData.personal.phoneNumber?.trim(),
      "phoneNumberCountryCode": formData.personal.phoneNumberCountryCode,
      "phoneNumberDialCode": formData.personal.phoneNumberDialCode,
      // Profile completion (DOB, address, employment) and SSN will be collected later via modals
    }
    try {
      await api.post('/v1/api/auth/signup', payload);
      showAlert('User created successfully!', 'success');
      setFormData({ // Reset form data
        personal: {},
        account: {},
        agreedToTerms: false,
      });
      navigate('/login');
    } catch (error) {
      showAlert(`Failed to create user: ${error.response?.data || error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const updateFormData = (step, data) => {
    setFormData(prev => ({
      ...prev,
      [step]: data
    }));
  };

  // Inline handlers replicated from PersonalInfoForm and AccountForm
  const handlePersonalChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...formData.personal, [name]: value };
    updateFormData('personal', newData);
  };
  const passwordRequirements = [
    { label: 'At least 8 characters', regex: /.{8,}/ },
    { label: 'At least one uppercase letter', regex: /[A-Z]/ },
    { label: 'At least one lowercase letter', regex: /[a-z]/ },
    { label: 'At least one number', regex: /[0-9]/ },
    { label: 'At least one special character', regex: /[!@#$%^&*(),.?":{}|<>]/ }
  ];
  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    if (name === 'password') {
      setPassword(value);
    }
    const newData = { ...formData.account, [name]: value };
    updateFormData('account', newData);
  };
  const checkRequirement = (regex) => regex.test(password);

  // Single page content rendered inline below

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Signup Header - Centered */}
      <Typography variant="h4" component="h1" sx={{ mb: 0.5, fontWeight: 'bold', textAlign: 'center' }}>
        Signup
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
        Fill in the form below to create your free account
      </Typography>

      <>
        {/* Inline single-page content: Personal Info */}
        <Grid container spacing={3} direction="column" sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Grid item xs={12}>
              <TextField required fullWidth name="firstName" label="First Name" value={formData.personal.firstName || ''} onChange={handlePersonalChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth name="lastName" label="Last Name" value={formData.personal.lastName || ''} onChange={handlePersonalChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField required fullWidth name="email" label="Email Address" type="email" value={formData.personal.email || ''} onChange={handlePersonalChange} />
            </Grid>
            <CountryPhoneInput data={formData.personal} updateData={(data) => updateFormData('personal', data)} />
    
            <Grid item xs={12}>
              <TextField required fullWidth name="username" label="Username" value={formData.account.username || ''} onChange={handleAccountChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required fullWidth name="password" label="Password"
                type={showPassword ? 'text' : 'password'} value={password} onChange={handleAccountChange}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ) }}
              />
              <Box sx={{ mt: 1, pl: 2 }}>
                <Typography variant="caption" display="block" gutterBottom>Password Requirements:</Typography>
                {passwordRequirements.map((req, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                    {checkRequirement(req.regex) ? (
                      <CheckCircleOutline color="success" fontSize="small" />
                    ) : (
                      <HighlightOff color="error" fontSize="small" />
                    )}
                    <Typography variant="caption" sx={{ ml: 1 }}>{req.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required fullWidth name="confirmPassword" label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'} value={formData.account.confirmPassword || ''} onChange={handleAccountChange}
                error={formData.account.confirmPassword && formData.account.confirmPassword !== password}
                helperText={formData.account.confirmPassword && formData.account.confirmPassword !== password ? 'Passwords do not match' : ''}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ) }}
              />
            </Grid>
        </Grid>
        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.agreedToTerms}
                onChange={(e) => updateFormData('agreedToTerms', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                By creating an account I confirm that I have read and agree to the Uniofunds Terms of Service and to the Uniofunds Privacy Policy. 
                I consent to the processing of my personal data as described in the 
                <Link href="#" onClick={(e) => { e.preventDefault(); window.open('/legal/documents/privacy', '_blank', 'noopener,noreferrer'); }}> Privacy Policy </Link>
                and to the 
                <Link href="#" onClick={(e) => { e.preventDefault(); window.open('/legal/documents/terms-and-conditions', '_blank', 'noopener,noreferrer'); }}> Terms of Service </Link>  
                governing use of the platform. 
              </Typography>
            }
          />
          <Divider sx={{ my: 3 }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              (
                !(formData.personal.firstName && formData.personal.lastName && formData.personal.email && formData.personal.phoneNumber)
              ) || (
                !(formData.account.username && formData.account.password && formData.account.confirmPassword)
              ) || (
                !formData.agreedToTerms
              ) || submitting
            }
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {submitting ? 'Creating...' : 'Create Account'}
          </Button>
        </Box>
      </>
    </Container>
  );
};

export default SignupPage;